import type { JSX } from 'react';
import { styled } from 'styled-components';
import { Title as DialogTitle } from '@radix-ui/react-dialog';
import { ModalCloseButton } from './ModalCloseButton';
import { ScreenReaderOnly } from '../ScreenReaderOnly';

// when the Modal opens we want to focus the first focusable element; if this were in natural order,
// the close button would be focused first. instead we move `ModalHeader` lower in the dom and use `order`
// here to visually place it at the top
const Header = styled.header<{
  $hideTitle: boolean;
  $hideCloseButon: boolean;
}>`
  display: flex;
  order: 1;
  padding: 0 var(--wui-space-05);
  padding-bottom: 0;
  gap: var(--wui-space-01);
  justify-content: ${({ $hideTitle }) =>
    $hideTitle ? 'flex-end' : 'space-between'}; /* ensure ModalCloseButton is right-aligned */

  /* DialogTitle creates an h2 element which inherits some margin */
  h2 {
    margin: 0;
    padding-right: ${({ $hideCloseButon }) => ($hideCloseButon ? '0' : 'var(--wui-space-03)')};
  }

  button {
    position: absolute;
    right: var(--wui-space-03);
    top: var(--wui-space-03);
  }
`;

const Title = styled(DialogTitle)`
  font-family: var(--wui-typography-heading-2-family);
  line-height: var(--wui-typography-heading-2-line-height);
  font-size: var(--wui-typography-heading-2-size);
  font-weight: var(--wui-typography-heading-2-weight);
  overflow-wrap: anywhere;
`;

export const ModalHeader = ({
  title,
  hideTitle,
  hideCloseButton,
}: {
  title: string;
  hideTitle: boolean;
  hideCloseButton: boolean;
}): JSX.Element => {
  // if we're hiding the title, we want to use a visually hidden element to
  // maintain a11y and avoid console warnings from radix
  const TitleComponent = hideTitle ? (
    <ScreenReaderOnly>
      <Title>{title}</Title>
    </ScreenReaderOnly>
  ) : (
    <Title>{title}</Title>
  );

  return (
    <Header
      $hideCloseButon={hideCloseButton}
      $hideTitle={hideTitle}
    >
      {TitleComponent}
      {hideCloseButton ? null : <ModalCloseButton />}
    </Header>
  );
};
